import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form, Input, Level, Toasts } from '@deseretbook/react-ui'
import { DeseretBook } from '../../../dbcom/api'
import { Logger, formatErrorMessage } from '../../../dbcom'
import { withCheckout } from '../CheckoutContext'

const logger = new Logger({ context: 'GiftCardForm' })

/**
 * Form for adding a gift card or promotion code to an order. Used as part of
 * the checkout page.
 */
class GiftCardForm extends Component {
  /**
   * Component constructor
   */
  constructor() {
    super()

    this.state = {
      /** If the form is currently being submitted. */
      isSubmitting: false,
      /** Default form values. */
      form: {
        value: '',
      },
    }

    this.onFormChange = this.onFormChange.bind(this)
    this.resetForm = this.resetForm.bind(this)
    this.applyPayment = this.applyPayment.bind(this)
    this.applyGiftCard = this.applyGiftCard.bind(this)
    this.applyPromo = this.applyPromo.bind(this)
  }

  /**
   * onChange handler for form inputs.
   */
  onFormChange({ target }) {
    const { name, value } = target

    this.setState(({ form }) => ({
      form: {
        ...form,
        [name]: value,
      },
    }))
  }

  /**
   * Resets the form's state.
   */
  resetForm() {
    this.setState({
      isSubmitting: false,
      form: {
        value: '',
      },
    })
  }

  /**
   * Parses the form to determine if it's a promo or a gift card. Once it figures
   * it out, it tries to apply the changes to the current order.
   */
  applyPayment() {
    const { paymentMethod } = this.props
    const { form } = this.state
    const giftCardRegex = /^r[a-z0-9]{12}$/i // Case insensitive 'R' followed by 12 alphanumeric characters.
    const isGiftCard = giftCardRegex.test(form.value)

    if (!form.value.length) {
      Toasts.warn('Missing Value', 'You must enter a gift card or promotion first.')

      return
    }

    if (paymentMethod && isGiftCard) {
      this.applyGiftCard()
    } else {
      this.applyPromo()
    }
  }

  /**
   * Applies a gift card to the current order.
   */
  applyGiftCard() {
    const {
      paymentMethod,
      order,
      updateOrder,
      startLoading,
      stopLoading,
    } = this.props
    const { form } = this.state

    startLoading(() => {
      this.setState({ isSubmitting: true }, async () => {
        try {
          const updatedOrder = await DeseretBook
            .order(order)
            .payment()
            .createGiftCardPayment({
              paymentMethodId: paymentMethod.id,
              sourceAttributes: {
                number: form.value,
              },
            })

          updateOrder(updatedOrder)
          this.resetForm()
          Toasts.success('Gift card successfully added')
          stopLoading()
        } catch ({ message, errors }) {
          Toasts.danger('Unable to apply gift card', formatErrorMessage(message, errors))
          logger.warning(`Unable to apply gift card: "${formatErrorMessage(message, errors)}"`)
          this.setState({ isSubmitting: false })
          stopLoading()
        }
      })
    })
  }

  /**
   * Applies a promotion code to the current order.
   */
  applyPromo() {
    const {
      order,
      updateOrder,
      startLoading,
      stopLoading,
    } = this.props
    const { form } = this.state

    startLoading(() => {
      this.setState({ isSubmitting: true }, async () => {
        try {
          await DeseretBook
            .order(order)
            .applyCouponCode({ couponCode: form.value })

          const updatedOrder = await DeseretBook
            .order(order)
            .get()

          updateOrder(updatedOrder, true)
          Toasts.success('Promo code applied') // TODO can we give a message or something?
          this.resetForm()
          stopLoading()
        } catch ({ message, errors }) {
          stopLoading()
          this.setState({ isSubmitting: false })
          Toasts.danger('Unable to apply promo code', formatErrorMessage(message, errors))
          logger.warning(`Unable to apply promo code: "${formatErrorMessage(message, errors)}"`, { form, errors })
        }
      })
    })
  }

  /**
   * Determines how to render the form.
   *
   * @returns {function} - GiftCardForm component
   */
  render() {
    const { status, paymentMethod } = this.props
    const { isSubmitting, form } = this.state

    return (
      <Form isSubmitting={isSubmitting} onSubmit={this.applyPayment}>
        <Input
          name="value"
          type="text"
          label={
            paymentMethod
              ? 'Gift Card or Promo Code'
              : 'Promo Code'
          }
          value={form.value}
          onChange={this.onFormChange}
          disabled={status.isLoading}
        />
        <Level>
          <Level.Right>
            <Form.SubmitButton
              color="primary"
              disabled={status.isLoading || !form.value.length}
            >
              Apply
            </Form.SubmitButton>
          </Level.Right>
        </Level>
      </Form>
    )
  }
}

GiftCardForm.propTypes = {
  /** Gift Card Payment Method Type */
  paymentMethod: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  order: PropTypes.shape({
    number: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  status: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.objectOf(PropTypes.bool),
  ])),
  updateOrder: PropTypes.func,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
}

GiftCardForm.defaultProps = {
  paymentMethod: null,
  order: {},
  status: {},
  updateOrder: () => {},
  startLoading: () => {},
  stopLoading: () => {},
}

export default withCheckout(GiftCardForm)
